import React, { useRef, useState } from 'react';
import InformeAnalisis from './InformeAnalisis';
import VistaPreviaCampana from './VistaPreviaCampana';
import { analizarCampana, crearCampana, descargarPlantillaExcel } from './campanasApi';
import { F } from './estilos';
import {
  ACCEPT_CABECERA, fechaLocal, formatoCabecera, nuevaClaveIdempotencia,
  programacionDemasiadoLejana, ZONA_POR_DEFECTO,
} from '../../utils/campanas';
import { motivoDelError } from '../../utils/errores';

const AYUDA_CABECERA = {
  IMAGE: 'JPEG o PNG, máximo 5 MB. El fichero será el mismo para todos los destinatarios.',
  VIDEO: 'MP4 o 3GPP, máximo 16 MB. El fichero será el mismo para todos los destinatarios.',
  DOCUMENT: 'PDF, máximo 25 MB. El fichero será el mismo para todos los destinatarios.',
};

const AVISO_29_DIAS = 'Con cabecera multimedia la campaña se puede programar como mucho a 29 días';

const ZONAS = [
  'Europe/Madrid', 'Atlantic/Canary', 'Europe/Lisbon', 'Europe/London', 'America/Mexico_City',
  'America/Bogota', 'America/Lima', 'America/Argentina/Buenos_Aires', 'UTC',
];

const conEstado = (estilo, deshabilitado) => (deshabilitado ? { ...estilo, ...F.deshabilitado } : estilo);

const Paso = ({ numero, titulo, hecho, children }) => (
  <section style={F.paso}>
    <div style={F.pasoCabecera}>
      <span style={F.pasoNumero(hecho)}>{hecho ? '✓' : numero}</span>
      <h4 style={F.pasoTitulo}>{titulo}</h4>
    </div>
    {children}
  </section>
);

const FilaResumen = ({ clave, valor }) => (
  <div style={F.resumenFila}>
    <span style={F.resumenClave}>{clave}</span>
    <span style={F.resumenValor}>{valor}</span>
  </div>
);

/** Nueva campaña (§10): nombre, plantilla apta, fichero, análisis y programación, con vista previa. */
const NuevaCampanaForm = ({ botId, zonaHoraria, plantillas, onCreada, onError, onCancelar }) => {
  const [nombre, setNombre] = useState('');
  const [plantilla, setPlantilla] = useState('');
  const [fichero, setFichero] = useState(null);
  const [cabecera, setCabecera] = useState(null);
  const [informe, setInforme] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [modo, setModo] = useState('ahora');
  const [fecha, setFecha] = useState('');
  const [zona, setZona] = useState(zonaHoraria || ZONA_POR_DEFECTO);
  const [creando, setCreando] = useState(false);
  const enviando = useRef(false);
  const claveIntento = useRef(null);
  const firmaPrevia = useRef(null);
  const firma = [nombre, plantilla, fichero?.name, fichero?.size, cabecera?.name, cabecera?.size, modo, fecha, zona].join('\0');
  if (firmaPrevia.current !== firma) {
    firmaPrevia.current = firma;
    claveIntento.current = null;
  }
  const [templateName, languageCode] = plantilla ? plantilla.split('|') : ['', ''];
  const elegida = plantillas.find((p) => `${p.name}|${p.language}` === plantilla);
  const formato = formatoCabecera(elegida);

  const elegirFichero = (f) => {
    setFichero(f || null);
    setInforme(null);
  };

  const soltar = (e) => {
    e.preventDefault();
    setArrastrando(false);
    if (e.dataTransfer?.files?.[0]) elegirFichero(e.dataTransfer.files[0]);
  };

  const cuerpoBase = () => {
    const cuerpo = new FormData();
    cuerpo.append('botId', String(botId));
    cuerpo.append('templateName', templateName);
    cuerpo.append('languageCode', languageCode);
    cuerpo.append('fichero', fichero);
    return cuerpo;
  };

  const analizar = async () => {
    if (!plantilla || !fichero) {
      onError('Elige una plantilla y un fichero.');
      return;
    }
    setAnalizando(true);
    const r = await analizarCampana(cuerpoBase());
    setAnalizando(false);
    if (r.ok) setInforme(r.data);
    else onError(motivoDelError(r.status, r.data, 'No se pudo analizar el fichero.'));
  };

  const crear = async () => {
    if (enviando.current) return;
    if (!nombre.trim()) {
      onError('Ponle un nombre a la campaña.');
      return;
    }
    if (formato && !cabecera) {
      onError('Sube el fichero de la cabecera');
      return;
    }
    if (modo === 'programar' && !fecha) {
      onError('Indica la fecha y la hora de envío.');
      return;
    }
    if (formato && modo === 'programar' && programacionDemasiadoLejana(fecha, zona, new Date())) {
      onError(AVISO_29_DIAS);
      return;
    }
    const cuerpo = cuerpoBase();
    cuerpo.append('nombre', nombre.trim());
    if (cabecera) cuerpo.append('cabecera', cabecera);
    if (modo === 'programar') {
      cuerpo.append('programadaPara', fecha);
      cuerpo.append('zonaHoraria', zona);
    }
    enviando.current = true;
    setCreando(true);
    if (!claveIntento.current) claveIntento.current = nuevaClaveIdempotencia();
    const r = await crearCampana(cuerpo, claveIntento.current);
    enviando.current = false;
    setCreando(false);
    if (r.status !== 0) claveIntento.current = null;
    if (r.ok) onCreada(r.data);
    else onError(motivoDelError(r.status, r.data, 'No se pudo crear la campaña.'));
  };

  const descargar = async () => {
    if (!(await descargarPlantillaExcel(botId, templateName, languageCode))) {
      onError('No se pudo descargar la plantilla de Excel.');
    }
  };

  const analisisValido = Boolean(informe) && informe.bloqueos.length === 0 && !informe.nivelMensajeria?.superado;
  const bloqueada = !analisisValido || creando;
  const envio = modo === 'ahora' ? 'En cuanto se cree' : fecha ? `${fechaLocal(fecha)} (${zona})` : 'Falta la fecha';

  return (
    <div className="campanas">
    <div className="campanas-layout" style={F.layout}>
      <div className="campanas-col" style={F.col}>
        <h3 style={F.title}>Nueva campaña</h3>
        <p style={F.subtitle}>Envía una plantilla aprobada a una lista de contactos, ahora o a la hora que elijas.</p>

        <Paso numero={1} titulo="Datos" hecho={Boolean(nombre.trim())}>
          <div style={F.field}>
            <label htmlFor="campana-nombre" style={F.label}>Nombre</label>
            <input
              id="campana-nombre" style={F.input} value={nombre} maxLength={150}
              placeholder="p.ej. Recordatorio de octubre" onChange={(e) => setNombre(e.target.value)}
            />
            <span style={F.hint}>Solo lo verás tú, en el listado de campañas.</span>
          </div>
        </Paso>

        <Paso numero={2} titulo="Plantilla" hecho={Boolean(plantilla)}>
          <div style={F.field}>
            <label htmlFor="campana-plantilla" style={F.label}>Plantilla</label>
            <select
              id="campana-plantilla"
              style={F.input}
              value={plantilla}
              onChange={(e) => { setPlantilla(e.target.value); setInforme(null); setCabecera(null); }}
            >
              <option value="">Elige una plantilla</option>
              {plantillas.map((p) => (
                <option key={`${p.name}|${p.language}`} value={`${p.name}|${p.language}`}>{`${p.name} (${p.language})`}</option>
              ))}
            </select>
            <span style={F.hint}>Solo aparecen las aprobadas y con variables con nombre.</span>
          </div>
          <button type="button" style={conEstado(F.btnSmall, !plantilla)} disabled={!plantilla} onClick={descargar}>
            Descargar plantilla de Excel
          </button>
        </Paso>

        {formato && (
          <Paso numero={3} titulo="Cabecera" hecho={Boolean(cabecera)}>
            <div style={F.field}>
              <label data-testid="campana-zona-cabecera" style={F.zonaFichero(false)}>
                <input
                  key={plantilla}
                  type="file"
                  accept={ACCEPT_CABECERA[formato]}
                  aria-label="Fichero de la cabecera"
                  style={F.oculto}
                  onChange={(e) => setCabecera(e.target.files?.[0] || null)}
                />
                <span>{cabecera ? '✓ Cabecera cargada — cambiar' : 'Elige el fichero de la cabecera'}</span>
                {cabecera && <span style={F.zonaFicheroNombre}>{cabecera.name}</span>}
              </label>
              <span style={F.hint}>{AYUDA_CABECERA[formato]}</span>
            </div>
          </Paso>
        )}

        <Paso numero={formato ? 4 : 3} titulo="Destinatarios" hecho={analisisValido}>
          <div style={F.field}>
            <label
              data-testid="campana-zona-fichero"
              style={F.zonaFichero(arrastrando)}
              onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={soltar}
            >
              <input
                type="file" accept=".xlsx,.csv" aria-label="Fichero" style={F.oculto}
                onChange={(e) => elegirFichero(e.target.files[0])}
              />
              <span>{fichero ? '✓ Fichero cargado — cambiar' : 'Arrastra aquí el fichero o haz clic para elegirlo'}</span>
              {fichero && <span style={F.zonaFicheroNombre}>{fichero.name}</span>}
            </label>
            <span style={F.hint}>Excel (.xlsx) o CSV con una columna msisdn y una columna por cada variable.</span>
          </div>
          <div style={{ marginBottom: 14 }}>
            <button type="button" style={conEstado(F.btnSmall, analizando)} disabled={analizando} onClick={analizar}>
              {analizando ? 'Analizando…' : 'Analizar'}
            </button>
          </div>
          {informe && <InformeAnalisis informe={informe} />}
        </Paso>

        <Paso numero={formato ? 5 : 4} titulo="Envío" hecho={analisisValido && (modo === 'ahora' || Boolean(fecha))}>
          <div className="campanas-segmentos" style={F.segmentos} role="radiogroup" aria-label="Cuándo enviar">
            <label style={F.segmento(modo === 'ahora')}>
              <input type="radio" name="modo" style={F.oculto} checked={modo === 'ahora'} onChange={() => setModo('ahora')} />
              Enviar ahora
            </label>
            <label style={F.segmento(modo === 'programar')}>
              <input type="radio" name="modo" style={F.oculto} checked={modo === 'programar'} onChange={() => setModo('programar')} />
              Programar
            </label>
          </div>
          {modo === 'programar' && (
            <div className="campanas-row" style={F.row}>
              <div style={{ ...F.field, flex: 1 }}>
                <label htmlFor="campana-fecha" style={F.label}>Fecha y hora</label>
                <input
                  id="campana-fecha" type="datetime-local" style={F.input} value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </div>
              <div style={{ ...F.field, flex: 1 }}>
                <label htmlFor="campana-zona" style={F.label}>Zona horaria</label>
                <input
                  id="campana-zona" list="campana-zonas" style={F.input} value={zona}
                  onChange={(e) => setZona(e.target.value)}
                />
                <datalist id="campana-zonas">
                  {ZONAS.map((z) => <option key={z} value={z} />)}
                </datalist>
              </div>
            </div>
          )}
        </Paso>

        <div className="campanas-acciones" style={F.actions}>
          <button type="button" style={F.btnGhost} onClick={onCancelar}>Cancelar</button>
          <button type="button" style={conEstado(F.btnPrimary, bloqueada)} disabled={bloqueada} onClick={crear}>
            {creando ? 'Creando…' : 'Crear campaña'}
          </button>
        </div>
      </div>

      <div className="campanas-preview" style={F.previewCol}>
        <VistaPreviaCampana plantilla={elegida} muestra={informe?.muestra} cabecera={cabecera} />
        <div style={F.resumen} data-testid="campana-resumen">
          <FilaResumen clave="Plantilla" valor={plantilla ? `${templateName} (${languageCode})` : 'Sin elegir'} />
          {formato && <FilaResumen clave="Cabecera" valor={cabecera ? cabecera.name : 'Sin fichero'} />}
          <FilaResumen clave="Destinatarios" valor={informe ? `${informe.filasValidas} destinatarios` : 'Analiza el fichero'} />
          <FilaResumen clave="Envío" valor={envio} />
        </div>
      </div>
    </div>
    </div>
  );
};

export default NuevaCampanaForm;
